import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { SearchRoundsService } from '../search-rounds-service';
import { SearchRoundsModel, type SearchRound } from '../../types';
import type { CurrentStep } from '../../enums';

@injectable()
export class SearchRoundsServiceImpl implements SearchRoundsService {
    constructor(
        @inject(SearchRoundsModel)
        private readonly _model: SearchRoundsModel,
    ) {}

    setSearchRounds(searchRounds: SearchRound[]): void {
        // 默认选中第一个搜索轮次
        const updatedRounds = searchRounds.map((round, index) => ({
            ...round,
            isSelected: index === 0,
        }));
        this._model.update(() => updatedRounds);
    }

    selectSearchRound(roundIndex: number): SearchRound {
        const currentRounds = this._model.modelData;
        if (roundIndex < 0 || roundIndex >= currentRounds.length) {
            throw new Error('Invalid search round index');
        }

        const updatedRounds = currentRounds.map((round, index) => ({
            ...round,
            isSelected: index === roundIndex,
        }));

        this._model.update(() => updatedRounds);
        return updatedRounds[roundIndex];
    }

    updateSearchQuery(roundIndex: number, newQuery: string): void {
        const currentRounds = this._model.modelData;
        if (roundIndex < 0 || roundIndex >= currentRounds.length) {
            throw new Error('Invalid search round index');
        }

        const updatedRounds = currentRounds.map((round, index) =>
            index === roundIndex
                ? { ...round, query: newQuery, isManual: false }
                : round,
        );

        this._model.update(() => updatedRounds);
    }

    updateSearchRoundStatus(roundIndex: number, status: SearchRound['status']): void {
        const currentRounds = this._model.modelData;
        if (roundIndex < 0 || roundIndex >= currentRounds.length) {
            throw new Error('Invalid search round index');
        }

        const updatedRounds = currentRounds.map((round, index) =>
            index === roundIndex
                ? { ...round, status }
                : round,
        );

        this._model.update(() => updatedRounds);
    }

    updateCurrentStep(roundIndex: number, currentStep: SearchRound['currentStep']): void {
        const currentRounds = this._model.modelData;
        if (roundIndex < 0 || roundIndex >= currentRounds.length) {
            throw new Error('Invalid search round index');
        }

        const updatedRounds = currentRounds.map((round, index) =>
            index === roundIndex
                ? { ...round, currentStep }
                : round,
        );

        this._model.update(() => updatedRounds);
    }

    updatePatentCount(roundIndex: number, patentCount: number): void {
        const currentRounds = this._model.modelData;
        if (roundIndex < 0 || roundIndex >= currentRounds.length) {
            throw new Error('Invalid search round index');
        }

        const updatedRounds = currentRounds.map((round, index) =>
            index === roundIndex
                ? { ...round, patentCount }
                : round,
        );

        this._model.update(() => updatedRounds);
    }

    updateFilteredPatentCount(roundIndex: number, step: CurrentStep, count: number): void {
        const currentRounds = this._model.modelData;
        if (roundIndex < 0 || roundIndex >= currentRounds.length) {
            throw new Error('Invalid search round index');
        }

        const updatedRounds = currentRounds.map((round, index) =>
            index === roundIndex
                ? {
                    ...round,
                    filteredPatentCounts: {
                        ...round.filteredPatentCounts,
                        [step]: count,
                    },
                }
                : round,
        );

        this._model.update(() => updatedRounds);
    }

    getSelectedSearchRound(): SearchRound | undefined {
        return this._model.modelData.find(round => round.isSelected);
    }

    getSelectedSearchRoundIndex(): number {
        return this._model.modelData.findIndex(round => round.isSelected);
    }

    getAllSearchRounds(): SearchRound[] {
        return this._model.modelData;
    }

    addSearchRound(searchRound: SearchRound): void {
        const currentRounds = this._model.modelData;
        // 新添加的轮次不选中，保持当前选中状态
        const updatedRounds = [...currentRounds, { ...searchRound, isSelected: false }];
        this._model.update(() => updatedRounds);
    }

    removeSearchRound(roundIndex: number): void {
        const currentRounds = this._model.modelData;
        if (roundIndex < 0 || roundIndex >= currentRounds.length) {
            throw new Error('Invalid search round index');
        }

        const updatedRounds = currentRounds.filter((_, index) => index !== roundIndex);
        // 如果删除的是选中的轮次，选中第一个轮次
        if (currentRounds[roundIndex].isSelected && updatedRounds.length > 0) {
            updatedRounds[0].isSelected = true;
        }
        this._model.update(() => updatedRounds);
    }
}
